/**
 * Keeps one result of an expensive load per server process for `ttlMs`.
 *
 * Next's own fetch cache is memory-only on this site (next.config.ts sets
 * experimental.isrFlushToDisk=false, which covers fetch-cache entries too)
 * and shares its 50 MB LRU with rendered pages -- a resume page is ~700 KB
 * of HTML + RSC -- so the big lists every page needs (the 1.8 MB resume
 * title list, the places list) were evicted and re-downloaded on nearly
 * every render: slower pages, and under load the downloads failed and the
 * pages 500'd. This copy lives outside that LRU.
 *
 * Concurrent callers share one in-flight load; a failed refresh keeps
 * serving the last good value (and throws only if there has never been one).
 */
export function processMemo<T>(load: () => Promise<T>, ttlMs: number) {
  let value: { data: T; loadedAt: number } | null = null;
  let inflight: Promise<T> | null = null;
  return {
    get(): Promise<T> {
      if (value && Date.now() - value.loadedAt < ttlMs) return Promise.resolve(value.data);
      inflight ??= load()
        .then((data) => {
          value = { data, loadedAt: Date.now() };
          return data;
        })
        .catch((err) => {
          if (value) return value.data;
          throw err;
        })
        .finally(() => {
          inflight = null;
        });
      return inflight;
    },
    /** The next get() reloads (still falling back to the current value if that fails). */
    expire(): void {
      if (value) value = { ...value, loadedAt: 0 };
    },
  };
}
